/** Review workflow helpers for Reviewer Studio Submit & Finish. */

export type ReviewStatusValue = string | null | undefined;

const TERMINAL_APPROVED = 'APPROVED';

export const normalizeReviewStatus = (status: ReviewStatusValue): string => (status ?? '').trim().toUpperCase();

export const isApprovedReviewStatus = (status: ReviewStatusValue): boolean =>
  normalizeReviewStatus(status) === TERMINAL_APPROVED;

/** Parse ISO strings or Mongo `{ $date: string }` into epoch ms. */
export const parseTimestampMs = (value: unknown): number | null => {
  if (value == null) return null;
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value < 1e12 ? value * 1000 : value;
  }
  if (typeof value === 'object' && !Array.isArray(value)) {
    const obj = value as Record<string, unknown>;
    if (obj.$date != null) return parseTimestampMs(obj.$date);
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const ms = Date.parse(trimmed);
    return Number.isNaN(ms) ? null : ms;
  }
  return null;
};

const REVIEW_TIMESTAMP_KEYS = [
  'reviewed_at',
  'last_review_at',
  'last_review_timestamp',
  'review_timestamp',
  'review_timestamp_utc',
  'last_review_timestamp_utc'
] as const;

const PROJECT_ACTIVITY_KEYS = ['updated_at', 'lastUpdated', 'last_updated', 'modified_at', 'last_modified'] as const;

const readFirstTimestamp = (source: Record<string, unknown>, keys: readonly string[]): number | null => {
  for (const key of keys) {
    const ms = parseTimestampMs(source[key]);
    if (ms != null) return ms;
  }
  return null;
};

const readNestedReviewTimestamp = (project: Record<string, unknown>): number | null => {
  const nested = project.review ?? project.reviewer_review ?? project.last_review;
  if (!nested || typeof nested !== 'object' || Array.isArray(nested)) return null;
  return readFirstTimestamp(nested as Record<string, unknown>, [
    'timestamp',
    'reviewed_at',
    'review_timestamp',
    'review_timestamp_utc',
    ...REVIEW_TIMESTAMP_KEYS
  ]);
};

const readLatestUploadUpdateMs = (project: Record<string, unknown>): number | null => {
  const uploads = project.uploads;
  if (!uploads || typeof uploads !== 'object') return null;

  let latest: number | null = null;
  const buckets = Object.values(uploads as Record<string, unknown>);
  for (const bucket of buckets) {
    if (!Array.isArray(bucket)) continue;
    for (const item of bucket) {
      if (!item || typeof item !== 'object') continue;
      const row = item as Record<string, unknown>;
      for (const key of ['update_date', 'upload_date']) {
        const ms = parseTimestampMs(row[key]);
        if (ms != null && (latest == null || ms > latest)) latest = ms;
      }
    }
  }
  return latest;
};

/** When the most recent reviewer verdict was recorded (if exposed on the project). */
export const getLastReviewTimestampMs = (project: Record<string, unknown> | null | undefined): number | null => {
  if (!project) return null;
  return readFirstTimestamp(project, REVIEW_TIMESTAMP_KEYS) ?? readNestedReviewTimestamp(project);
};

/** Latest known project activity (metadata updates, uploads, etc.). */
export const getProjectLastActivityMs = (project: Record<string, unknown> | null | undefined): number | null => {
  if (!project) return null;

  const candidates = [readFirstTimestamp(project, PROJECT_ACTIVITY_KEYS), readLatestUploadUpdateMs(project)].filter(
    (ms): ms is number => ms != null
  );

  if (candidates.length === 0) return null;
  return Math.max(...candidates);
};

/**
 * True when project activity is newer than the last recorded review (typical after re-run / upload / edit).
 */
export const hasProjectChangedSinceLastReview = (project: Record<string, unknown> | null | undefined): boolean => {
  const reviewMs = getLastReviewTimestampMs(project);
  const activityMs = getProjectLastActivityMs(project);
  if (activityMs == null) return false;
  if (reviewMs == null) return false;
  return activityMs > reviewMs;
};

/**
 * Whether Submit & Finish should accept a new reviewer verdict.
 * - Non-approved statuses: always allowed (including REJECTED and READY_FOR_REVIEW).
 * - APPROVED: allowed only when the project changed after that approval.
 */
export const canReviewerSubmitReview = (
  reviewStatus: ReviewStatusValue,
  project?: Record<string, unknown> | null
): boolean => {
  if (!isApprovedReviewStatus(reviewStatus)) return true;
  return hasProjectChangedSinceLastReview(project ?? null);
};

export const shouldReopenReviewAfterProjectUpdate = (reviewStatus: ReviewStatusValue): boolean =>
  isApprovedReviewStatus(reviewStatus);
